import React, { useState } from "react";
import "../styles/DetailsPage.css";
import { useNavigate, useParams } from "react-router-dom";

const giftCardData: {
  [key: string]: {
    image: string;
    title: string;
    description: string;
    amounts: {
      value: number;
      label: string;
      price: number;
      rewards: number;
    }[];
  };
} = {
  uber: {
    image:
      "https://cdn1.codashop.com/S/content/mobile/images/product-tiles/ubertds_tile.jpg",
    title: "Uber Eats",
    description:
      "Get your Uber Eats Gift Codes in seconds! Simply select your preferred amount, choose the mode of payment that’s most convenient for you, and receive your Uber Eats gift code instantly.",
    amounts: [
      {
        value: 25,
        label: "$25 Uber Eats Gift Code",
        price: 25.0,
        rewards: 0.12,
      },
      {
        value: 50,
        label: "$50 Uber Eats Gift Code",
        price: 50.0,
        rewards: 0.25,
      },
      {
        value: 100,
        label: "$100 Uber Eats Gift Code",
        price: 100.0,
        rewards: 0.5,
      },
      {
        value: 200,
        label: "$200 Uber Eats Gift Code",
        price: 200.0,
        rewards: 1.0,
      },
    ],
  },
  netflix: {
    image:
      "https://cdn1.codashop.com/S/content/mobile/images/product-tiles/netflixtds_tile.jpg",
    title: "Netflix",
    description:
      "Instantly stream your favorite shows and movies with Netflix Gift Cards. Choose an amount and pay easily!",
    amounts: [
      {
        value: 25,
        label: "$25 Netflix Gift Code",
        price: 25.0,
        rewards: 0.12,
      },
      {
        value: 50,
        label: "$50 Netflix Gift Code",
        price: 50.0,
        rewards: 0.25,
      },
      {
        value: 100,
        label: "$100 Netflix Gift Code",
        price: 100.0,
        rewards: 0.5,
      },
      {
        value: 200,
        label: "$200 Netflix Gift Code",
        price: 200.0,
        rewards: 1.0,
      },
    ],
  },
  doordash: {
    image:
      "https://cdn1.codashop.com/S/content/mobile/images/product-tiles/doordashtds_tile.jpg",
    title: "DoorDash",
    description:
      "Order from your favorite local restaurants with DoorDash Gift Cards. Delivered instantly to your inbox!",
    amounts: [
      {
        value: 25,
        label: "$25 Doordash Gift Code",
        price: 25.0,
        rewards: 0.12,
      },
      {
        value: 50,
        label: "$50 Doordash Gift Code",
        price: 50.0,
        rewards: 0.25,
      },
      {
        value: 100,
        label: "$100 Doordash Gift Code",
        price: 100.0,
        rewards: 0.5,
      },
      {
        value: 200,
        label: "$200 Doordash Gift Code",
        price: 200.0,
        rewards: 1.0,
      },
    ],
  },
};

const UberEatsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const giftData = id ? giftCardData[id] : null;
  const [selectedAmount, setSelectedAmount] = useState<{
    value: number;
    label: string;
    price: number;
    rewards: number;
  } | null>(null);
  const [email, setEmail] = useState("");
  const [emailPromo, setEmailPromo] = useState(false);
  const [smsPromo, setSmsPromo] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const amounts = giftData?.amounts ?? [];

  const handleAmountSelect = (amount: {
    value: number;
    label: string;
    price: number;
    rewards: number;
  }) => {
    setSelectedAmount(amount);
  };

  const handleBuyNow = () => {
    if (selectedAmount && email && termsAccepted) {
      alert(`Purchasing ${selectedAmount.label} for ${email}`);
    } else {
      alert("Please select an amount, enter your email, and accept the terms.");
    }
  };

  return (
    <div className="uber-eats-page">
      {/* Left Sidebar */}
      <div className="sidebar">
        <div className="sidebar-content">
          {giftData && (
            <>
              <img
                src={giftData.image}
                alt={`${giftData.title} Logo`}
                className="uber-eats-logo"
              />
              <h1>{giftData.title}</h1>
              <div className="features-badges">
                <div className="badge">
                  <img
                    src="/icons/bolt-shield.svg"
                    alt="Instant Delivery"
                    className="badge-icon"
                  />
                  <span>Instant Delivery</span>
                </div>
                <div className="badge">
                  <img
                    src="/icons/check-shield.svg"
                    alt="Official Distributor"
                    className="badge-icon"
                  />
                  <span>Official Distributor</span>
                </div>
              </div>
              <p className="description">{giftData.description}</p>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Rewards Banner */}
        <div className="rewards-banner">
          🎁 Earn up to 0.5% Rewards from Uber Eats and Payment Methods
        </div>

        {/* Voucher Selection */}
        <div className="section">
          <h2>1 Select voucher</h2>
          <div className="voucher-options">
            {amounts.map((amount) => (
              <div
                key={amount.value}
                className={`voucher-card ${
                  selectedAmount?.value === amount.value ? "selected" : ""
                }`}
                onClick={() => handleAmountSelect(amount)}
              >
                <p className="voucher-label">{amount.label}</p>
                <p className="voucher-price">From ${amount.price.toFixed(2)}</p>
                <p className="voucher-rewards">
                  🎁 {amount.rewards.toFixed(2)} Rewards
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Method */}
        <div className="section">
          <h2>2 Select payment</h2>
          <div className="payment-options">
            <div className="payment-card selected">
              <img
                src="https://cdn1.codashop.com/S/content/common/images/mno/CARDPAYMENTS_AU_CHNL_LOGO.webp"
                alt="Visa Mastercard"
                className="payment-logo"
              />
              <p>Card Payments</p>
            </div>
          </div>
        </div>

        {/* Buy Section */}
        <div className="section">
          <h2>3 Buy!</h2>
          <p className="email-note">
            Make sure your email address is correct, we will use it to deliver
            your voucher code, and get the 0.5% GiftShop Rewards saved to your
            email
          </p>
          <input
            type="email"
            placeholder="Please enter the email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="email-input"
          />
          <div className="promo-options">
            <label>
              <input
                type="checkbox"
                checked={emailPromo}
                onChange={() => setEmailPromo(!emailPromo)}
              />
              Yes, send me messages with exclusive GiftShop news and promotions.
            </label>
            <label>
              <input
                type="checkbox"
                checked={smsPromo}
                onChange={() => setSmsPromo(!smsPromo)}
              />
              Yes, I would like to receive news & promotions via SMS.
            </label>
          </div>
          <div className="terms">
            <h3>TERMS & CONDITIONS:</h3>
            <p>By clicking ‘Buy Now’,</p>
            <label>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={() => setTermsAccepted(!termsAccepted)}
              />
              <div>
                <p>
                  (i) acknowledge that I have read, understand, and agree to
                  GiftShop's Terms and Conditions and Privacy Policy, and{" "}
                </p>
                <p>
                  (ii) consent to the immediate delivery of the digital content
                  upon conclusion of the purchase, and I acknowledge that I will
                  lose my right of withdrawal from the purchase.{" "}
                </p>
              </div>
            </label>
          </div>
          <button
            className="buy-now-button"
            onClick={handleBuyNow}
            disabled={!selectedAmount || !email || !termsAccepted}
          >
            BUY NOW
          </button>
        </div>
      </div>
    </div>
  );
};

export default UberEatsPage;
